/**
 * "Update now" flow (OFFLINE-DISTRIBUTION-PLAN.md §3.3): confirm -> ask the
 * server to start -> poll `/api/updates/status` every few seconds while the
 * updater backs up, installs and restarts -> reload when the new version
 * answers, or show the rollback log. Ported from ../lims's
 * `app/update-flow.tsx` onto Pospak's plain-Tailwind dialog style.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, X } from 'lucide-react';
import { toast } from 'sonner';
import type { LocalUpdateStatusDto } from '@pospak/shared';
import { ReleaseNotes } from '../components/ReleaseNotes';
import { ApiClientError } from '../lib/api';
import { fetchUpdateStatus, useApplyUpdateMutation } from '../lib/updates-api';

const POLL_MS = 5_000;
const TIMEOUT_MS = 10 * 60_000;
const RELOAD_DELAY_MS = 1_500;

type Phase = 'confirm' | 'starting' | 'applying' | 'done' | 'failed';

function Modal({ title, description, onClose, closable, children }: { title: string; description?: string; onClose: () => void; closable: boolean; children: ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4">
      <div className="card w-full max-w-lg p-6">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-bold">{title}</h2>
            {description ? <p className="mt-1 text-sm text-ink2">{description}</p> : null}
          </div>
          {closable ? (
            <button type="button" className="btn-ghost h-8 w-8 flex-none p-0" onClick={onClose} aria-label="Close">
              <X size={16} aria-hidden />
            </button>
          ) : null}
        </div>
        {children}
      </div>
    </div>
  );
}

export interface UpdateApplyDialogProps {
  status: LocalUpdateStatusDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function elapsedLabel(startedAt: number, now: number): string {
  const seconds = Math.max(0, Math.floor((now - startedAt) / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/** Mounted only while open, so every close starts the next attempt from a
 * clean `confirm` phase without a reset effect. */
export function UpdateApplyDialog({ status, open, onOpenChange }: UpdateApplyDialogProps) {
  if (!open || !status.pendingUpdate) return null;
  return <UpdateApplyDialogBody status={status} onOpenChange={onOpenChange} />;
}

function UpdateApplyDialogBody({ status, onOpenChange }: Omit<UpdateApplyDialogProps, 'open'>) {
  const pending = status.pendingUpdate;
  const [phase, setPhase] = useState<Phase>('confirm');
  const [failure, setFailure] = useState<string | null>(null);
  const [offline, setOffline] = useState(false);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const fromVersion = useRef(status.runningVersion);
  const apply = useApplyUpdateMutation();

  useEffect(() => {
    if (phase !== 'applying' || startedAt === null) return;
    let cancelled = false;
    const tick = (): void => {
      setNow(Date.now());
      if (Date.now() - startedAt > TIMEOUT_MS) {
        setFailure('The update did not finish within 10 minutes. Check the server: C:\\Pospak\\last-update.json and logs\\update-*.log.');
        setPhase('failed');
        return;
      }
      fetchUpdateStatus()
        .then((fresh) => {
          if (cancelled) return;
          setOffline(false);
          if (fresh.runningVersion !== fromVersion.current) {
            setPhase('done');
            toast.success(`Updated to version ${fresh.runningVersion}. Reloading…`);
            window.setTimeout(() => window.location.reload(), RELOAD_DELAY_MS);
            return;
          }
          if (fresh.lastUpdateFailed) {
            setFailure(fresh.lastUpdateLog ?? 'The updater reported a failure and rolled back.');
            setPhase('failed');
          }
        })
        .catch(() => {
          if (!cancelled) setOffline(true);
        });
    };
    const timer = window.setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [phase, startedAt]);

  if (!pending) return null;

  const confirm = (): void => {
    setPhase('starting');
    fromVersion.current = status.runningVersion;
    apply.mutate(
      { version: pending.version },
      {
        onSuccess: () => {
          setStartedAt(Date.now());
          setNow(Date.now());
          setPhase('applying');
        },
        onError: (err) => {
          setFailure(err instanceof ApiClientError ? err.message : 'Could not start the update.');
          setPhase('failed');
        },
      },
    );
  };

  const locked = phase === 'applying' || phase === 'starting' || phase === 'done';

  const title = phase === 'done' ? 'Update complete' : phase === 'failed' ? 'Update did not complete' : `Update to version ${pending.version}`;
  const description =
    phase === 'confirm'
      ? `This server runs ${status.runningVersion}. Applying the update downloads the signed package, backs up the database, installs the new version and restarts the server. Everyone signed in will lose their connection for about a minute — pick a quiet moment.`
      : phase === 'starting'
        ? 'Asking the server to start the update…'
        : phase === 'applying'
          ? 'The updater is running. This page checks the server every few seconds and reloads itself when the new version answers.'
          : phase === 'done'
            ? 'The new version is running. Reloading this page…'
            : 'The server is still on its previous version.';

  return (
    <Modal title={title} description={description} closable={!locked} onClose={() => onOpenChange(false)}>
      <div className="space-y-4">
        {phase === 'confirm' ? (
          <div className="max-h-56 overflow-y-auto rounded-lg border border-border bg-surface2 p-3">
            <ReleaseNotes notes={pending.notes} />
          </div>
        ) : null}

        {phase === 'applying' && startedAt !== null ? (
          <div className="flex items-center gap-3 rounded-lg border border-border bg-surface2 p-3 text-sm text-ink2">
            <Loader2 size={16} className="flex-none animate-spin text-brand" aria-hidden />
            <span>
              {offline ? 'Server is restarting — waiting for it to come back' : 'Updating'} · <span className="num">{elapsedLabel(startedAt, now)}</span>
            </span>
          </div>
        ) : null}

        {phase === 'done' ? (
          <div className="flex items-center gap-3 rounded-lg border border-border bg-ok/10 p-3 text-sm text-ok">
            <CheckCircle2 size={16} className="flex-none" aria-hidden /> Reloading…
          </div>
        ) : null}

        {phase === 'failed' ? (
          <div className="space-y-2 rounded-lg border border-border bg-danger/10 p-3">
            <p className="flex items-center gap-2 text-sm font-medium text-danger">
              <AlertTriangle size={16} className="flex-none" aria-hidden /> Update failed
            </p>
            <pre className="num max-h-48 overflow-auto whitespace-pre-wrap text-[11px] leading-4 text-ink2">{failure}</pre>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          {phase === 'confirm' ? (
            <>
              <button type="button" className="btn-ghost" onClick={() => onOpenChange(false)}>
                Not now
              </button>
              <button type="button" className="btn-primary" onClick={confirm}>
                <RefreshCw size={16} aria-hidden /> Update and restart
              </button>
            </>
          ) : phase === 'failed' ? (
            <button type="button" className="btn-ghost" onClick={() => onOpenChange(false)}>
              Close
            </button>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}

export interface ReleaseNotesDialogProps {
  version: string;
  notes: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ReleaseNotesDialog({ version, notes, open, onOpenChange }: ReleaseNotesDialogProps) {
  if (!open) return null;
  return (
    <Modal title={`What's new in ${version}`} closable onClose={() => onOpenChange(false)}>
      <div className="max-h-[60vh] overflow-y-auto">
        <ReleaseNotes notes={notes} />
      </div>
      <div className="mt-4 flex justify-end">
        <button type="button" className="btn-ghost" onClick={() => onOpenChange(false)}>
          Close
        </button>
      </div>
    </Modal>
  );
}
